import { useRef, useState } from 'react';
import { toast } from 'sonner';
import { saveEmailDraft, reviewEmailDraft, submitEmailDraft, draftError, editableDraft } from '../lib/emailDrafts';

export const useConsultationEmailReview = (initial, auth, onSent) => {
  const [draft, setDraft] = useState(initial), [saved, setSaved] = useState(null);
  const current = useRef(null), lock = useRef(false);
  const [form, setForm] = useState({ to_email: initial.to_email, cc: initial.cc, subject: initial.subject, message: initial.message });
  const [reviewed, setReviewed] = useState(false), [confirmed, setConfirmed] = useState(false);
  const [busy, setBusy] = useState(''), [error, setError] = useState(''), [blocked, setBlocked] = useState(false);
  const edit = (key, value) => { setForm((old) => ({ ...old, [key]: value })); setReviewed(false); setConfirmed(false); setError(''); };
  const persist = async () => {
    const data = await saveEmailDraft(current.current, {
      to_email: form.to_email, cc: form.cc, subject: form.subject, body: form.message, body_format: 'plain', kind: 'consultation', source_id: initial.summary_id,
      refs: [{ type: 'consultation_summary', id: initial.summary_id }], library_ids: [], keep_upload_ids: [],
    }, [], auth);
    current.current = data; setSaved(data); return data;
  };
  const save = async () => {
    if (lock.current || blocked) return;
    lock.current = true; setBusy('save'); setError('');
    try { await persist(); setReviewed(false); setConfirmed(false); toast.success('Email draft saved'); }
    catch (e) { setError(draftError(e)); }
    finally { lock.current = false; setBusy(''); }
  };
  const review = async () => {
    if (lock.current || blocked) return;
    lock.current = true; setBusy('review'); setError(''); setConfirmed(false); setReviewed(false);
    try {
      const data = await reviewEmailDraft(await persist(), auth);
      current.current = data; setSaved(data);
      setDraft({ ...initial, html: data.reviewed_html, to_email: data.to_email, cc: data.cc, subject: data.subject, attachment: data.attachments[0], savedDraft: data });
      setForm({ to_email: data.to_email, cc: data.cc, subject: data.subject, message: data.body }); setReviewed(true);
    } catch (e) { setError(draftError(e)); }
    finally { lock.current = false; setBusy(''); }
  };
  const send = async (localTime = null, onScheduled) => {
    if (lock.current || blocked || !reviewed || !confirmed) return;
    lock.current = true; setBusy(localTime ? 'schedule' : 'send'); setError('');
    try {
      const data = await submitEmailDraft(current.current, auth, localTime);
      current.current = data; setSaved(data);
      if (data.status === 'scheduled') onScheduled?.(data);
      else if (data.status === 'sent') onSent({ success: true, sent_to: data.to_email });
      else { setBlocked(!editableDraft(data)); setReviewed(false); setConfirmed(false); setError(data.delivery_note); }
    } catch (e) {
      if (!e.response || e.response.status >= 500 || e.response.status === 409) setBlocked(true);
      setError(draftError(e));
    } finally { lock.current = false; setBusy(''); }
  };
  return { draft, saved, form, edit, review, save, send, reviewed, confirmed, setConfirmed, busy, error, blocked };
};